import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ChevronRight, RefreshCcw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { summarize } from './_shared';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import AccountCleanupPanel from './AccountCleanupPanel';

export default function CollectionsPanel() {
  const [collections, setCollections] = useState([]);
  const [activeColl, setActiveColl] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docTotal, setDocTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [editorValue, setEditorValue] = useState('');

  const reload = async () => {
    try { setCollections(await adminApi.collections()); }
    catch { toast.error('Could not load collections'); }
  };
  useEffect(() => { reload(); }, []);

  const openColl = async (name) => {
    setActiveColl(name); setSelected(null); setEditorValue('');
    try {
      const data = await adminApi.browse(name, { limit: 200 });
      setDocs(data.items); setDocTotal(data.total);
    } catch { toast.error('Failed to load documents'); }
  };

  const openDoc = (d) => { setSelected(d); setEditorValue(JSON.stringify(d, null, 2)); };

  const saveDoc = async () => {
    if (!selected) return;
    try {
      const next = JSON.parse(editorValue);
      const id = next.id || selected.id;
      const updated = await adminApi.patchDoc(activeColl, id, next);
      toast.success('Saved');
      setSelected(updated);
      setEditorValue(JSON.stringify(updated, null, 2));
      setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
  };

  const deleteDoc = async () => {
    if (!selected) return;
    if (!(await openConfirm({
      title: `Delete document ${selected.id}?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'destructive',
    }))) return;
    try {
      await adminApi.deleteDoc(activeColl, selected.id);
      setDocs((prev) => prev.filter((d) => d.id !== selected.id));
      setSelected(null); setEditorValue('');
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <AccountCleanupPanel />
      <section className="flex flex-1 min-h-[400px] -m-6 h-[calc(100%+3rem)]">
      <aside className="w-56 shrink-0 border-r border-border bg-panel-header/20 p-2 overflow-auto scroll-thin">
        <div className="mb-1 px-2 text-[10px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
          <span>Collections</span>
          <button onClick={reload} className="hover:text-foreground"><RefreshCcw className="h-3 w-3" /></button>
        </div>
        <ul className="space-y-0.5">
          {collections.map((c) => (
            <li key={c.name}>
              <button onClick={() => openColl(c.name)} className={cn(
                'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-[12.5px]',
                activeColl === c.name ? 'bg-primary/10 text-foreground' : 'hover:bg-secondary/60 text-foreground/80'
              )} data-testid={`admin-collection-${c.name}`}>
                <span>{c.name}</span>
                <span className="font-mono-stat text-[10.5px] text-muted-foreground">{c.count}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="w-80 shrink-0 border-r border-border overflow-auto scroll-thin">
        {!activeColl ? (
          <div className="p-6 text-[12px] text-muted-foreground">Select a collection on the left.</div>
        ) : (
          <ul className="divide-y divide-border">
            <li className="sticky top-0 z-10 flex items-center justify-between bg-panel-header/60 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground backdrop-blur">
              <span>{activeColl}</span>
              <span className="font-mono-stat">{docs.length} / {docTotal}</span>
            </li>
            {docs.map((d) => (
              <li key={d.id || d.user_id || d._oid}>
                <button onClick={() => openDoc(d)} className={cn(
                  'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-secondary/60',
                  selected?.id === d.id && 'bg-primary/10'
                )}>
                  <ChevronRight className={cn('mt-0.5 h-3 w-3 text-muted-foreground transition-transform', selected?.id === d.id && 'rotate-90 text-primary')} />
                  <div className="min-w-0">
                    <div className="truncate text-[12.5px] font-medium">{summarize(d)}</div>
                    <div className="font-mono-stat text-[10.5px] text-muted-foreground">{d.id}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-1 min-w-0 flex-col">
        {!selected ? (
          <div className="m-auto text-[12px] text-muted-foreground">Select a document to view or edit.</div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border bg-panel-header/40 px-3 py-2">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{activeColl}</div>
                <div className="font-mono-stat text-[11.5px] text-foreground">{selected.id}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button onClick={saveDoc} size="sm" className="h-7 gap-1.5"><Save className="h-3.5 w-3.5" /> Save</Button>
                <Button onClick={deleteDoc} variant="ghost" size="sm" className="h-7 gap-1.5 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
            <Textarea
              value={editorValue}
              onChange={(e) => setEditorValue(e.target.value)}
              spellCheck={false}
              className="flex-1 resize-none rounded-none border-0 bg-background font-mono-stat text-[12px] leading-relaxed focus-visible:ring-0"
            />
          </>
        )}
      </section>
      </section>
    </div>
  );
}
